import React, { useState, useRef, useCallback } from "react";
import type { PresenceElement } from "./types";
import AgentIcon from "./AgentIcon";
import VoiceInput from "../chat/VoiceInput";

interface DialogProps {
  element: PresenceElement;
  onDismiss: (id: string) => void;
  onReply?: (elementId: string, text: string) => void;
}

/**
 * Lightweight floating conversation bubble.
 * Quick exchange between agent and operator, dissolves when resolved.
 */
export default function Dialog({ element, onDismiss, onReply }: DialogProps): React.ReactElement {
  const [input, setInput] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);
  const messages = element.messages ?? [];

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim()) return;
    onReply?.(element.id, input.trim());
    setInput("");
  };

  const handleVoiceTranscript = useCallback((transcript: string) => {
    const next = String(transcript || "").trim();
    if (!next) return;
    setInput((prev) => (prev.trim() ? `${prev.trim()} ${next}` : next));
    inputRef.current?.focus();
  }, []);

  return (
    <div
      className="agent-dialog pointer-events-auto w-64 rounded-lg shadow-md overflow-hidden flex flex-col"
      style={{
        backgroundColor: "var(--c-surface)",
        border: "1px solid var(--c-border)",
        maxHeight: 280,
      }}
    >
      {/* Header */}
      <div
        className="flex items-center justify-between px-3 py-2 shrink-0"
        style={{ borderBottom: "1px solid var(--c-border)" }}
      >
        <div className="flex items-center gap-1.5">
          <AgentIcon size={12} />
          <span
            className="text-[10px] font-medium uppercase tracking-wider"
            style={{ color: "var(--c-text-faint)" }}
          >
            Agent
          </span>
        </div>
        <button
          onClick={() => onDismiss(element.id)}
          className="p-0.5 rounded transition-colors"
          style={{ color: "var(--c-text-faint)" }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = "var(--c-hover)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = "";
          }}
        >
          <svg
            width="10"
            height="10"
            viewBox="0 0 10 10"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
          >
            <path d="M8 2L2 8M2 2l6 6" />
          </svg>
        </button>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-3 py-2 flex flex-col gap-2">
        {/* Initial agent message */}
        {element.text && (
          <div className="text-xs leading-relaxed" style={{ color: "var(--c-text-secondary)" }}>
            {element.text}
          </div>
        )}
        {messages.map((msg, i) => (
          <div
            key={i}
            className={`text-xs leading-relaxed px-2 py-1.5 rounded-md ${
              msg.role === "agent" ? "" : "self-end"
            }`}
            style={{
              backgroundColor: msg.role === "agent" ? "transparent" : "var(--c-surface-2)",
              color: "var(--c-text-secondary)",
              maxWidth: "85%",
            }}
          >
            {msg.text}
          </div>
        ))}
      </div>

      {/* Input */}
      <form onSubmit={handleSubmit} className="shrink-0 px-2 pb-2">
        <div className="flex items-center gap-1.5">
          <input
            ref={inputRef}
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Reply..."
            className="w-full px-2.5 py-1.5 rounded text-xs outline-none"
            style={{
              backgroundColor: "var(--c-surface-2)",
              color: "var(--c-text)",
              border: "1px solid var(--c-border)",
            }}
          />
          <VoiceInput onTranscript={handleVoiceTranscript} />
        </div>
      </form>
    </div>
  );
}
